import { featureCollection } from "@turf/helpers";
import C from "../Constants";

export function useMapbox(map) {
  const allFeaturesMap = {};
  let clickCallback = null;

  const addSources = () => {
    map.addSource(C.SOURCE_ID.RG_HISTORY_SOURCE, {
      type: "geojson",
      data: featureCollection([]),
    });

    map.addSource(C.SOURCE_ID.RG_OBJECT_DISPLAY_AREA, {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [],
      },
    });
  };

  const addLayers = () => {
    map.addLayer({
      id: C.LAYER_ID.RG_OBJECT_LAYER,
      type: "symbol",
      minzoom: 5,
      source: C.SOURCE_ID.RG_HISTORY_SOURCE,
      layout: {
        "icon-image": "{icon}",
        "icon-allow-overlap": ["step", ["zoom"], false, 9, true],
        "icon-ignore-placement": ["step", ["zoom"], false, 9, true],
        "text-max-width": 20,
        "text-field": "{name}",
        "text-size": 14,
        "text-offset": [1.5, 0],
        "text-justify": "left",
        "text-anchor": "left",
        "text-optional": true,
        "text-allow-overlap": ["step", ["zoom"], false, 9, true],
        "text-ignore-placement": ["step", ["zoom"], false, 9, true],
        "icon-rotate": { type: "identity", property: "heading" },
      },
      paint: {
        "text-color": "#0ED463",
        "text-halo-color": "#000000",
        "text-halo-width": 1,
      },
      filter: ["==", ["get", "source"], "radar"],
    });

    map.addLayer({
      id: C.LAYER_ID.RG_OBJECT_FLAME_LAYER,
      type: "symbol",
      minzoom: 5,
      source: C.SOURCE_ID.RG_HISTORY_SOURCE,
      layout: {
        "icon-image": "rg-target-vessel-safe-default",
        "icon-allow-overlap": ["step", ["zoom"], false, 9, true],
        "icon-ignore-placement": ["step", ["zoom"], false, 9, true],
      },
      paint: {
        "text-color": "#0ED463",
        "text-halo-color": "#000000",
        "text-halo-width": 1,
      },
      filter: [
        "all",
        ["==", ["get", "source"], "radar"],
        ["==", ["get", "isTrack"], false],
      ],
    });

    map.addLayer({
      id: C.LAYER_ID.RG_MY_VESSEL_LAYER,
      type: "symbol",
      minzoom: 5,
      source: C.SOURCE_ID.RG_HISTORY_SOURCE,
      layout: {
        "icon-image": "{icon}",
        "icon-allow-overlap": ["step", ["zoom"], false, 9, true],
        "icon-ignore-placement": ["step", ["zoom"], false, 9, true],
        "text-field": "{name}",
        "text-size": 14,
        "text-offset": [0, 2],
        "text-optional": true,
        "text-allow-overlap": ["step", ["zoom"], false, 9, true],
        "text-ignore-placement": ["step", ["zoom"], false, 9, true],
        "icon-rotate": { type: "identity", property: "heading" },
      },
      paint: {
        "text-color": "#54B1FC",
        "text-halo-color": "#000000",
        "text-halo-width": 1,
      },
      filter: [
        "all",
        ["==", ["get", "source"], "ais"],
        ["==", ["get", "isMyVessel"], true],
      ],
    });

    map.addLayer({
      id: C.LAYER_ID.RG_OTHER_VESSEL_LAYER,
      type: "symbol",
      minzoom: 5,
      source: C.SOURCE_ID.RG_HISTORY_SOURCE,
      layout: {
        "icon-image": "{icon}",
        "icon-allow-overlap": ["step", ["zoom"], false, 9, true],
        "icon-ignore-placement": ["step", ["zoom"], false, 9, true],
        "text-field": "{name}",
        "text-size": 14,
        "text-offset": [0, 2],
        "text-optional": true,
        "text-allow-overlap": ["step", ["zoom"], false, 9, true],
        "text-ignore-placement": ["step", ["zoom"], false, 9, true],
        "icon-rotate": { type: "identity", property: "heading" },
      },
      paint: {
        "text-color": "#ffffff",
        "text-halo-color": "#000000",
        "text-halo-width": 1,
      },
      filter: [
        "all",
        ["==", ["get", "source"], "ais"],
        ["==", ["get", "isMyVessel"], false],
      ],
    });

    map.addLayer({
      id: C.LAYER_ID.RG_DISPLAY_AREA_LAYER,
      type: "line",
      source: C.SOURCE_ID.RG_OBJECT_DISPLAY_AREA,
      paint: {
        "line-color": "#F616E0",
        "line-width": 2,
      },
    });
  };

  const onClickRgObject = evt => {
    if (!evt.features) return;
    const feature = evt.features[0];
    if (!feature) return;

    const [lon, lat] = feature.geometry.coordinates;
    const { x, y } = map.project([lon, lat]);

    if (clickCallback) {
      clickCallback(feature, x, y);
    }
  };

  const onRgObjectClick = cb => {
    clickCallback = cb;
  };

  const addClickEvents = () => {
    map.on("click", C.LAYER_ID.RG_OBJECT_LAYER, onClickRgObject);
    setMousePointer(C.LAYER_ID.RG_OBJECT_LAYER);
  };

  const jumpTo = (center, zoom) => {
    map.jumpTo({
      center: center,
      zoom: zoom,
    });
  };

  const setSourceData = (sourceId, geojson) => {
    const source = map.getSource(sourceId);
    if (source) {
      source.setData(geojson);
    }
  };

  const updateFeature = (objectId, feature) => {
    if (feature === null) {
      delete allFeaturesMap[objectId];
    } else {
      allFeaturesMap[objectId] = feature;
    }
    const source = map.getSource(C.SOURCE_ID.RG_HISTORY_SOURCE);
    if (!source) return;

    source.setData({
      type: "FeatureCollection",
      features: Object.values(allFeaturesMap),
    });
  };

  const getFeature = objectId => {
    return allFeaturesMap[objectId] || null;
  };

  const setMousePointer = layerId => {
    map.on("mouseenter", layerId, () => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", layerId, () => {
      map.getCanvas().style.cursor = "";
    });
  };

  const setLayerVisibility = (layerId, isVisible) => {
    if (map.getLayer(layerId)) {
      const visibility = isVisible ? "visible" : "none";
      map.setLayoutProperty(layerId, "visibility", visibility);
    }
  };

  const onMapMove = cb => {
    map.on("move", () => {
      cb();
    });
  };

  const onZoomChange = cb => {
    map.on("zoom", () => {
      cb(map.getZoom());
    });
  };

  return {
    addSources,
    addLayers,
    addClickEvents,
    setSourceData,
    jumpTo,
    updateFeature,
    setLayerVisibility,
    onRgObjectClick,
    getFeature,
    onMapMove,
    onZoomChange,
  };
}
